"use client"

import styles from "./styles/calendar.module.scss";
import FlatIcon from "../FlatIcon";
import moment from "moment-timezone";
import React, {ReactNode, useMemo} from "react";
import classNames from "classnames";
import {IReactiveEventContentItem} from "../../../stores/SchoolFeedStore";
import {useTranslation} from "react-i18next";
import i18n from "../../../internationalization/i18n";
import {currentDateFormat, currentMonthFormat} from "./utils";

const weekdaysShort = moment.weekdaysShort();

export function MonthCalendar(props: {
    currentMonth: string,
    currentDate: string,
    setCurrentDate: (date: string) => void,
    events: IReactiveEventContentItem[],
    prevMonth: () => void,
    nextMonth: () => void,
}) {
    const { t } = useTranslation('translation', {i18n});

    const daysInMonth = useMemo(() => {
        const days: ReactNode[] = [];
        const currentMonthMoment = moment(props.currentMonth, currentMonthFormat);
        for (let i = 1; i <= currentMonthMoment.daysInMonth(); i++) {
            const todayMoment = moment(); // currentDate is just the currently selected date, not necessarily the actual today
            const currentDayString = `${i}-${props.currentMonth}`;
            const isToday = currentDayString === todayMoment.format(currentDateFormat);

            const events = props.events.filter(event => {
                const start = moment(event.json_data.event.start);
                const end = moment(event.json_data.event.end);
                // all day events end at midnight the next day, so the end day is excluded from the isBetween check
                const inclusivity = event.json_data.event.all_day ? '[)' : '[]';
                return moment(`${i}-${currentMonthMoment.format("M-YYYY")}`, "D-M-YYYY").isBetween(start, end, "days", inclusivity);
            })

            const dayClassName = classNames({
                [styles.day]: true,
                [styles.dayToday]: isToday,
                [styles.daySelected]: props.currentDate === moment(currentDayString, currentDateFormat).format(currentDateFormat),
                [styles.dayPopulated]: events.length > 0,
                [styles.td]: true,
            })

            days.push(<div key={currentDayString} className={dayClassName}>
                <button className={"notranslate"} onClick={() => {
                    props.setCurrentDate(moment(currentDayString, currentDateFormat).format(currentDateFormat))
                }}>{i}</button>
            </div>)
        }
        return days;

    }, [props.currentDate, props.events]);


    const rows = useMemo(() => {
        const firstDay = moment(props.currentMonth, currentMonthFormat)
            .startOf("month")
            .format("d");

        const blanks: ReactNode[] = [];
        for (let i = 0; i < Number(firstDay); i++) {
            blanks.push(<div className={styles.td} key={i}/>)
        }

        const totalSlots = [...blanks, ...daysInMonth];
        const r: ReactNode[] = [];
        let c: ReactNode[] = [];

        totalSlots.forEach((row, i) => {
            if (i % 7 !== 0) {
                c.push(row); // if index not equal 7 that means not go to next week
            } else {
                r.push(c); // when reach next week we contain all td in last week to rows
                c = []; // empty container
                c.push(row); // in current loop we still push current row to new container
            }
            if (i === totalSlots.length - 1) { // when end loop we add remain date
                r.push(c);
            }
        });

        return r;
    }, [daysInMonth])

    return <div className={styles.monthCalendar}>
        <div className={styles.controls}>
            <button
                aria-label={"Go to previous month"}
                onClick={props.prevMonth}
                style={{transform: "rotate(180deg)"}}
            >
                <FlatIcon name={"flaticon-next"} ariaHidden/>
            </button>
            <span>
                                {moment(props.currentMonth, currentMonthFormat).format("MMMM YYYY")}
                            </span>
            <button aria-label={"Go to next month"} onClick={props.nextMonth}>
                <FlatIcon name={"flaticon-next"} ariaHidden/>
            </button>
        </div>
        <div className={styles.table}>
            <div className={styles.tr}>
                {weekdaysShort.map(day => (
                    <div className={`notranslate ${styles.th}`} key={day}>
                        {t(day)}
                    </div>
                ))}
            </div>
            {rows.map((d, i) => {
                return <div className={styles.tr} key={i}>{d}</div>;
            })}
        </div>

    </div>
}